import { Box, Skeleton } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

interface ExpertexVideoProps {
  src: string;
  onLoadedData: () => void;
  onError: (error: any) => void; //Improved error handling
  placeholder?: string;
  CanPlayOnHover?: boolean;
  hovered: boolean;
}

const ExpertexVideo: React.FC<ExpertexVideoProps> = ({
  src,
  onLoadedData,
  onError,
  placeholder = "/Images/VideoSuite/video-thumb.jpg",
  CanPlayOnHover = false,
  hovered = false, // Changed default to false
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false); // Track load completion

  useEffect(() => {
    if (!wrapperRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: "0px",
        threshold: 0.05, // Increased threshold
      }
    );

    observer.observe(wrapperRef.current);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (CanPlayOnHover && hovered && !isPlaying && videoRef.current && videoLoaded) {
      videoRef.current
        .play()
        .then(() => setIsPlaying(true))
        .catch(err => {
          console.error("Video playback error:", err);
          onError(err); // Report the error
        });
    } else if (CanPlayOnHover && !hovered && isPlaying && videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  }, [hovered, videoLoaded]); // Added videoLoaded to dependency array

  useEffect(() => {
    if (videoRef.current && shouldLoad) {
      videoRef.current.onloadeddata = () => {
          setVideoLoaded(true);
          onLoadedData();
      };
      videoRef.current.onerror = (error) => {
        onError(error)
        console.error("Video load error:", error); // Log the error for debugging.
      };
    }
  }, [shouldLoad]);


  return (
    <div
      ref={wrapperRef}
      style={{
        width: "100%",
        position: "relative",
        aspectRatio: "16 / 9",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
      }}
    >
      {!videoLoaded && (
        <Skeleton
          className="skeleton-background"
          variant="rectangular"
          width="100%"
          height="100%"
          animation="wave"
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
        />
      )}
      {shouldLoad ? (
        <video
          ref={videoRef}
          src={src}
          muted
          playsInline
          preload="metadata" // Changed to metadata for quicker initial load
          controls={false}
          loop={false}
          style={{
            top: 0,
            left: 0,
            width: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      ) : (
        <img
          src={placeholder}
          alt="video preview"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
          }}
        />
      )}
    </div>
  );
};

export default ExpertexVideo;